const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/utils.tsx';
import React, { ReactNode, type JSX } from 'react';
import { CheckCircle } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { css, cx } from '@emotion/css';
import { Chip } from 'App/components/Chip/Chip';
import textMap from 'App/pages/DashboardPage/Components/appointmentCards/translations';
import { StructuredAppointment } from '@cistec/common_web/defs/kisim';
import { FormatMessageFn } from '@cistec/common_web/defs/generalDefs';

export interface ColorSettings {
    icon?: IconSettings;
    color?: string;
    backgroundColor?: string;
    opacity?: string;
}

export interface IconSettings {
    element: string | ReactNode;
    color?: string;
}

export type Severity = 'info' | 'success' | 'warning' | 'danger' | 'caution' | 'black';

export const getIcon = (icon?: IconSettings): JSX.Element | false => {
    let iconElement: JSX.Element | false;
    if (icon) {
        const iconWrapper = css`
            height: 22px;
            width: 22px;
            border-radius: 50%;
            display: flex;
            color: ${icon.color || 'inherit'};
            line-height: unset;
        `;

        iconElement = (
            <div className={iconWrapper}>
                {typeof icon.element === 'string' ? <span className={cx(`${icon.element}`, 'icon')} /> : icon.element}
            </div>
        );
    } else {
        iconElement = false;
    }
    return iconElement;
};

const getAlertColors = (severity: Severity): ColorSettings => {
    switch (severity) {
        case 'info':
            return {
                icon: {
                    element: 'icons8-error-circle-filled',
                    color: '#1C7CD6',
                },
                color: '#003A70',
                backgroundColor: '#E7F2FA',
            };
        case 'success':
            return {
                icon: {
                    element: <CheckCircle />,
                    color: '#278639',
                },
                color: '#004D00',
                backgroundColor: '#EAF5EC',
            };
        case 'danger':
            return {
                icon: {
                    element: 'icons8-error-filled',
                    color: '#D50000',
                },
                color: '#520000',
                backgroundColor: '#FCE1E1',
            };
        case 'warning':
            return {
                icon: {
                    element: 'icons8-warning-filled',
                    color: '#F76707',
                },
                color: '#8D3902',
                backgroundColor: '#FEEEE5',
            };
        case 'caution':
            return {
                icon: {
                    element: 'icons8-info-filled',
                    color: '#FCDC16',
                },
                color: '#665500',
                backgroundColor: '#FEF8D9',
            };
        default:
            return {
                color: '#003A70',
                backgroundColor: '#E7F2FA',
            };
    }
};
const getChipColor = (severity: Severity): ColorSettings => {
    switch (severity) {
        case 'info':
            return {
                color: '#003A70',
                backgroundColor: '#DDEBF8',
            };
        case 'success':
            return {
                color: '#195725',
                backgroundColor: '#DFF0E2',
            };
        case 'caution':
            return {
                color: '#665500',
                backgroundColor: '#FDF3C0',
            };
        case 'warning':
            return {
                color: '#8D3902',
                backgroundColor: '#FDE8DA',
            };
        case 'danger':
            return {
                color: '#520000',
                backgroundColor: '#FFCCCC',
            };
        case 'black':
            return {
                color: '#212121',
                backgroundColor: '#E2E2E2',
                opacity: '0.64',
            };
        default:
            return {
                color: '#003A70',
                backgroundColor: '#DDEBF8',
            };
    }
};

const getSnackbarColors = (severity: Severity) => {
    switch (severity) {
        case 'info':
            return {
                icon: {
                    element: 'icons8-error-circle-filled',
                    color: '#1C7CD6',
                },
                color: '#003A70',
                backgroundColor: '#E7F2FA',
            };
        case 'success':
            return {
                icon: {
                    element: <CheckCircle />,
                    color: '#278639',
                },
                color: '#004D00',
                backgroundColor: '#EAF5EC',
            };
        case 'danger':
            return {
                icon: {
                    element: 'icons8-error-filled',
                    color: '#D50000',
                },
                color: '#520000',
                backgroundColor: '#FCE1E1',
            };
        case 'warning':
            return {
                icon: {
                    element: 'icons8-warning-filled',
                    color: '#F76707',
                },
                color: '#8D3902',
                backgroundColor: '#FEEEE5',
            };
        case 'caution':
            return {
                icon: {
                    element: <InfoIcon />,
                    color: '#FCDC16',
                },
                color: '#665500',
                backgroundColor: '#FEF8D9',
            };
        default:
            return {
                color: '#212121',
                backgroundColor: '#FFFFFF',
            };
    }
};

export const getColorSettings = (
    componentType: 'alert' | 'snackbar' | 'chip',
    color?: Severity | ColorSettings
): ColorSettings => {
    const defaultColor = {
        color: 'inherit',
        backgroundColor: 'transparent',
    };

    if (!color) {
        return defaultColor;
    }

    if (typeof color === 'object') {
        return {
            color: color?.color || defaultColor.color,
            backgroundColor: color?.backgroundColor || defaultColor.backgroundColor,
        };
    }

    switch (componentType) {
        case 'alert':
            return getAlertColors(color);
        case 'snackbar':
            return getSnackbarColors(color);
        case 'chip':
            return getChipColor(color);
    }
};

export const getAppointmentStatusChip = (appointment: StructuredAppointment, formatMessage: FormatMessageFn) =>
    !appointment.STATUS?.NAME || appointment.STATUS?.NAME?.toLowerCase() === 'provisorisch' ? (
        <Chip color={'warning'}>{formatMessage(textMap.provisionally)}</Chip>
    ) : (
        <Chip color={'success'}>{formatMessage(textMap.planned)}</Chip>
    );
